import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckoutBreadcrumbs from "./CheckoutBreadcrumbs";
import { getCheckout, setCheckout } from "../utils/buyNowStorage";
import api from "../api/axios";
import { loadRazorpayScript, openRazorpay } from "../utils/razorpay";

const PaymentPage = () => {
  const navigate = useNavigate();
  const checkout = getCheckout();
  const [method, setMethod] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!checkout?.items?.length || !checkout?.addressId) {
      navigate("/", { replace: true });
    }
  }, []);

  /* Calculate total from checkout items */
  const total = checkout?.items?.reduce(
    (sum, item) => sum + item.price * item.qty,
    0
  ) || 0;
  const shipping = total > 500 ? 0 : 49;
  const grandTotal = total + shipping;

  const handlePay = async () => {
    if (!method) return alert("Select payment method");

    if (method === "razorpay") {
      return handleRazorpayPayment();
    }

    /* COD flow */
    setCheckout({ ...checkout, method });
    navigate("/order-success");
  };

  const handleRazorpayPayment = async () => {
    try {
      setLoading(true);

      /* 1. Create Razorpay order via backend */
      const { data } = await api.post("/payments/create-order", {
        amount: grandTotal,
      });

      if (!data.success) {
        alert("Failed to initiate payment. Please try again.");
        return;
      }

      /* 2. Load & open Razorpay */
      await loadRazorpayScript();

      openRazorpay({
        amount: data.amount,
        order_id: data.orderId,
        description: `Order Payment - ₹${grandTotal.toLocaleString("en-IN")}`,
        themeColor: "#ff9900",
        onSuccess: async (response) => {
          const verifyRes = await api.post("/payments/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });

          if (verifyRes.data.success) {
            setCheckout({
              ...checkout,
              method: "razorpay",
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
            });
            navigate("/order-success");
          } else {
            alert("Payment verification failed. Please contact support.");
          }
        },
        onDismiss: () => setLoading(false),
      });
    } catch (err) {
      console.error("Razorpay error:", err);
      alert("Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!checkout) return null;

  return (
    <Box sx={{ maxWidth: 500, mx: "auto", mt: 4 }}>
      <CheckoutBreadcrumbs active={2} />

      <Paper sx={{ p: 3 }}>
        <Typography fontWeight={700}>Payment Method</Typography>

        {/* Order Total Summary */}
        <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2, my: 2 }}>
          <Typography variant="body2">Subtotal: ₹{Number(total).toLocaleString("en-IN")}</Typography>
          <Typography variant="body2">
            Shipping: {shipping === 0 ? "FREE" : `₹${shipping.toLocaleString("en-IN")}`}
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
            Total: ₹{Number(grandTotal).toLocaleString("en-IN")}
          </Typography>
        </Box>

        <RadioGroup
          value={method}
          onChange={(e) => setMethod(e.target.value)}
        >
          <FormControlLabel value="cod" control={<Radio />} label="Cash on Delivery" />
          <FormControlLabel value="razorpay" control={<Radio />} label="Pay Online (Card / UPI / Net Banking)" />
        </RadioGroup>

        <Button
          fullWidth
          sx={{ mt: 3 }}
          variant="contained"
          onClick={handlePay}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : method === "razorpay" ? (
            `Pay ₹${Number(grandTotal).toLocaleString("en-IN")}`
          ) : (
            "Place Order"
          )}
        </Button>
      </Paper>
    </Box>
  );
};

export default PaymentPage;